import * as React from 'react';
import cn from 'classnames';
import MediaQuery from 'react-responsive';
import SwipeableViews from 'react-swipeable-views';

import { useTheme } from '../../../theme';
import { format, removeDotsFromDate, TABLET_MIN_WIDTH } from '../../../utils';
import { FlightFragment, FlightSegmentStatus } from '@websky/graphql';
import { getDurationString } from '../../../Results/utils';

import FlightInfoCardPoint from '../FlightInfoCardPoint/FlightInfoCardPoint';
import FlightInfoCardHint, { hintType } from '../FlightInfoCardHint/FlightInfoCardHint';
import SegmentServices from '../SegmentServices/SegmentServices';
import Notification from '../../../BaseComponents/Notification';
import Amenities from '../../../Amenities';
import { useTranslation } from 'react-i18next';

import AircraftPopup from '../../../AircraftPopup/components/AircraftPopup';
import { CdnIcon } from '@shared/ui';
import { StartCheckInNotification } from '@features/start-check-in';

interface Props {
	flight: FlightFragment;
	segmentId: string;
	onChangeIndex: (index: number) => void;
	orderId: string;
}

const FlightInfoCardTabs: React.FC<Props> = ({ flight, segmentId, onChangeIndex, orderId }) => {
	const { FlightInfoCardTabs: css } = useTheme('FlightInfoCard');

	const { t } = useTranslation('FlightInfoCard');

	return (
		<section>
			<SwipeableViews index={+segmentId} onChangeIndex={onChangeIndex}>
				{flight.segments.map(({ segment }) => {
					const { id: aircraftId, icon: aircraftIcon, name: aircraftName } = segment.aircraft ?? {};
					const isSegmentCanceled = segment.status === FlightSegmentStatus.Canceled;
					const hasNotConfirmedStatus = !!segment.status && segment.status !== FlightSegmentStatus.Confirmed;

					const segmentStatusNotification = hasNotConfirmedStatus ? (
						<Notification
							className={cn(css.tabs__segmentStatus)}
							type="context"
							status={isSegmentCanceled ? 'error' : 'warning'}
							text={t(segment.status)}
						/>
					) : null;

					return (
						<div key={segment.id} className={cn(css.tab__container)}>
							<MediaQuery minWidth={TABLET_MIN_WIDTH}>
								<div>
									<div className={cn(css.tabs__segmentGroupWrapper)}>
										<div className={cn(css.tabs__segment, css.tabs__segmentDate)}>
											<img
												className={cn(css.tabs__logo)}
												src={segment.operatingAirline.icon}
												alt={segment.operatingAirline.name}
											/>
											<div>
												<h2 className={cn(css.tabs__header)}>{t('Departure')}</h2>
												<p className={cn(css.tabs__value)}>
													{removeDotsFromDate(
														format(segment.departure.date, 'dd MMM, EEEEEE')
													)}
												</p>
											</div>
										</div>
										<div className={cn(css.tabs__segment)}>
											<h2 className={cn(css.tabs__header)}>
												{t('Flight')}, {segment.operatingAirline.name}
											</h2>
											<p className={cn(css.tabs__value)}>
												{segment.operatingAirline.iata}-{segment.flightNumber}
											</p>
										</div>
										<div className={cn(css.tabs__segment, css.tabs__segmentRoute)}>
											<FlightInfoCardPoint
												time={segment.departure.time}
												airport={segment.departure.airport}
												terminal={segment.departure.terminal}
												departure={true}
											/>
											<div className={cn(css.tabs__timeWrapper)}>
												<div className={cn(css.tabs__timeInRoute)}>
													{getDurationString(segment.duration)}
												</div>
												<div className={cn(css.tabs__timeLine)} />
											</div>
											<FlightInfoCardPoint
												time={segment.arrival.time}
												airport={segment.arrival.airport}
												terminal={segment.arrival.terminal}
											/>
										</div>
									</div>

									{segmentStatusNotification}

									<SegmentServices flight={flight} segmentId={segmentId} orderId={orderId} />
								</div>
							</MediaQuery>

							<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
								<div className={cn(css.tabs__segmentGroupWrapper_mobile)}>
									<div className={cn(css.tabs__header)}>
										<img
											className={cn(css.tabs__logo)}
											src={segment.marketingAirline.icon}
											alt={segment.marketingAirline.name}
										/>
										<div>
											<div className={cn(css.tabs__headerWrapper)}>
												<div className={cn(css.tabs__segment, css.tabs__segmentDate)}>
													<p className={cn(css.tabs__value)}>
														{t('Departure')} {format(segment.departure.date, 'dd MMM EE')}
													</p>
												</div>
												<div className={cn(css.tabs__segment)}>
													<p className={cn(css.tabs__value)}>
														{t('Flight')} {segment.marketingAirline.iata}-
														{segment.flightNumber}
													</p>
												</div>
											</div>
											<p className={cn(css.tabs__value)}>
												{[
													segment.marketingAirline.name,
													aircraftName,
													getDurationString(segment.duration)
												]
													.filter(Boolean)
													.join(', ')}
											</p>
										</div>
									</div>

									<div className={cn(css.tabs__segment, css.tabs__segmentRoute)}>
										<FlightInfoCardPoint
											time={segment.departure.time}
											airport={segment.departure.airport}
											terminal={segment.departure.terminal}
											departure={true}
											mobile={true}
										/>
										<FlightInfoCardPoint
											time={segment.arrival.time}
											airport={segment.arrival.airport}
											terminal={segment.arrival.terminal}
											mobile={true}
										/>

										<div className={css.tab__airline}>
											{aircraftIcon && (
												<div>
													<img src={aircraftIcon} alt="" />
												</div>
											)}
											<div className={css.tab__airline_detail}>
												{aircraftId && (
													<AircraftPopup
														id={aircraftId}
														name={aircraftName}
														operatingIata={segment.marketingAirline.iata}
														renderButton={click => {
															return (
																<div onClick={click} className={css.tab__airline_name}>
																	<div>{aircraftName}</div>{' '}
																	<CdnIcon icon="Info/Details" />
																</div>
															);
														}}
													/>
												)}

												{segment.flightInfo?.amenities && (
													<Amenities amenities={segment.flightInfo.amenities} />
												)}
											</div>
										</div>
									</div>

									{segmentStatusNotification}

									<SegmentServices flight={flight} segmentId={segmentId} orderId={orderId} />
								</div>
							</MediaQuery>

							<div className={css.tabs__segmentGroupCheckin}>
								<StartCheckInNotification />
							</div>

							{segment.stops.length > 0 && (
								<FlightInfoCardHint type={hintType.StopHint} segment={segment} />
							)}
						</div>
					);
				})}
			</SwipeableViews>
		</section>
	);
};

export default FlightInfoCardTabs;
